import React, { useState } from 'react';
import { 
  X, 
  Trash2, 
  AlertTriangle, 
  HardDriveDownload, 
  Mail, 
  ShieldAlert, 
  ShieldCheck,
  CheckCircle2, 
  FileText, 
  Info,
  ExternalLink,
  Ban,
  Bot,
  Lock
} from 'lucide-react';
import { ProtectedEmail } from './ProtectedEmail';
import { Booking, Expense, Insight, ServiceType } from '../types';
import { triggerQuickJSONBackup } from '../utils/backupManager';
import { getBusinessSettings } from '../utils/businessSettings';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookings?: Booking[];
  expenses?: Expense[];
  insights?: Insight[];
  streams?: ServiceType[];
  onOpenExportCenter?: () => void;
  showToast?: (msg: string, type?: 'success' | 'info' | 'error') => void;
}

export function DeleteAccountModal({
  isOpen,
  onClose,
  bookings = [],
  expenses = [],
  insights = [],
  streams = [],
  onOpenExportCenter,
  showToast
}: DeleteAccountModalProps) {
  const [backedUp, setBackedUp] = useState(false);
  const [acknowledgedNoRefunds, setAcknowledgedNoRefunds] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Anti-Bot Verification State
  const [isHumanVerified, setIsHumanVerified] = useState(false);
  const [isVerifyingHuman, setIsVerifyingHuman] = useState(false);

  if (!isOpen) return null;

  const settings = getBusinessSettings();
  const companyName = settings.companyName || 'AzuraOS Founder Workspace';
  const ownerEmail = settings.email || 'user@azuraos.app';

  const handleDownloadBackup = () => {
    setIsDownloading(true);
    setTimeout(() => {
      triggerQuickJSONBackup(bookings, expenses, insights, streams);
      setIsDownloading(false);
      setBackedUp(true);
      if (showToast) showToast('🛡️ Full workspace backup JSON downloaded successfully!', 'success');
    }, 400);
  };

  const handleToggleHumanVerify = (checked: boolean) => {
    if (!checked) {
      setIsHumanVerified(false);
      return;
    }

    setIsVerifyingHuman(true);
    setTimeout(() => {
      setIsVerifyingHuman(false);
      setIsHumanVerified(true);
      if (showToast) showToast('🛡️ Anti-bot human signature verified!', 'success');
    }, 800);
  };

  const handleGenerateMailto = () => {
    if (!isHumanVerified) {
      if (showToast) showToast('🤖 Please complete the anti-bot verification first.', 'error');
      return;
    }

    if (!backedUp) {
      if (showToast) showToast('⚠️ Please back up your workspace data before sending deletion request.', 'info');
    }

    const subject = encodeURIComponent(`Account & Data Deletion Request - ${companyName}`);
    const bodyText = `Hello AzuraOS Support Team,

I am formally requesting the permanent deletion of my account, profile, and all associated workspace data from your servers and cloud vaults.

ACCOUNT & WORKSPACE DETAILS:
- Company / Studio Name: ${companyName}
- Account Email: ${ownerEmail}
- Date of Request: ${new Date().toLocaleDateString()}

CONFIRMATIONS & ACKNOWLEDGEMENTS:
[x] I confirm that I have downloaded and stored my own data backups (CSV/JSON/PDF) from the Export Center prior to submitting this request.
[x] I explicitly acknowledge and accept the No Refunds Policy. I understand that account deletion terminates all active or remaining access without entitlement to any full or partial refunds, whether under Beta Access or future subscription tiers.
[x] I have completed anti-bot verification.

Please process this deletion request and confirm once completed.

Thank you,
${settings.senderName || companyName}`;

    const mailtoUrl = `mailto:support@azuraos.app?subject=${subject}&body=${encodeURIComponent(bodyText)}`;
    window.location.href = mailtoUrl;

    if (showToast) showToast('📧 Opened mail client with pre-filled deletion request to support@azuraos.app', 'success');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-md animate-fade-in overflow-y-auto">
      <div className="relative w-full max-w-xl my-auto max-h-[90vh] overflow-y-auto bg-[#151819] border border-rose-500/30 rounded-3xl shadow-2xl text-white scrollbar-thin scrollbar-thumb-rose-500/20">
        
        {/* Warning Header Glow Accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 via-amber-500 to-rose-600" />
        <div className="absolute -top-16 -right-16 w-40 h-40 bg-rose-500/10 rounded-full blur-2xl pointer-events-none" />

        {/* Modal Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white bg-[#0C0E0F]/60 hover:bg-[#0C0E0F] rounded-full border border-white/10 transition-all cursor-pointer z-20"
          title="Close Modal"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8 space-y-6">
          
          {/* Title Header */}
          <div className="space-y-2 border-b border-[#1F2426] pb-5">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 rounded-md bg-rose-500/15 border border-rose-500/30 text-rose-400 text-[10px] font-mono font-bold uppercase tracking-wider flex items-center gap-1.5">
                <ShieldAlert size={13} /> Account Governance
              </span>
              <span className="text-[11px] font-mono text-gray-400">GDPR &amp; Data Rights</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white flex items-center gap-2.5">
              <Trash2 className="text-rose-400" size={24} />
              <span>Delete Account &amp; Data Request</span>
            </h2>
            <p className="text-xs text-gray-400">
              Submit a formal request to eradicate your account profile, business records, and cloud vaults.
            </p>
          </div>

          {/* CRITICAL WARNING CARD 1: BACKUP YOUR DATA FIRST */}
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
                <HardDriveDownload size={18} />
              </div>
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-amber-300 uppercase tracking-wider">
                  Mandatory: Export &amp; Backup Your Data First
                </h4>
                <p className="text-xs text-amber-200/90 leading-relaxed">
                  Account deletion is <strong>irreversible</strong>. Once your account is purged, all historical bookings, client invoices, profit margin analytics, and journal records will be permanently erased.
                </p>
              </div>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
              <button
                type="button"
                onClick={handleDownloadBackup}
                disabled={isDownloading}
                className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-xs rounded-xl shadow transition flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
              >
                {isDownloading ? (
                  <span>Preparing JSON...</span>
                ) : (
                  <>
                    <HardDriveDownload size={14} />
                    <span>Download JSON Backup Now</span>
                  </>
                )}
              </button>

              {onOpenExportCenter && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenExportCenter();
                  }}
                  className="px-4 py-2 bg-[#151819] hover:bg-[#1F2426] border border-[#2A2F31] text-gray-200 font-bold text-xs rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <FileText size={13} className="text-gray-400" />
                  <span>Open Export Center</span>
                </button>
              )}
            </div>
          </div>

          {/* CRITICAL WARNING CARD 2: NO REFUNDS POLICY */}
          <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl space-y-2">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-rose-500/20 border border-rose-500/40 text-rose-400 flex items-center justify-center shrink-0 mt-0.5">
                <Ban size={18} />
              </div>
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-rose-300 uppercase tracking-wider">
                  Notice: Strict No Refunds Policy
                </h4>
                <p className="text-xs text-rose-200/90 leading-relaxed">
                  Please take note: A strict <strong>No Refunds policy</strong> applies to all accounts and access tiers, whether under Beta Access, lifetime founder licenses, or future subscription plans. Deleting your account terminates all remaining access without entitlement to any full or partial refunds.
                </p>
              </div>
            </div>

            <label className="flex items-center gap-2.5 pt-2 text-xs text-gray-300 font-medium cursor-pointer select-none">
              <input
                type="checkbox"
                checked={acknowledgedNoRefunds}
                onChange={(e) => setAcknowledgedNoRefunds(e.target.checked)}
                className="w-4 h-4 rounded border-[#2A2F31] bg-[#0C0E0F] text-rose-500 focus:ring-rose-500 cursor-pointer"
              />
              <span className="text-rose-200">I have read, understood &amp; accept the No Refunds Policy</span>
            </label>
          </div>

          {/* STEP 3: ANTI-BOT VERIFICATION CARD */}
          <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-white flex items-center gap-2">
                <Bot size={16} className={isHumanVerified ? "text-emerald-400" : "text-cyan-400"} />
                Anti-Bot Human Verification
              </span>
              <span className="text-[10px] text-gray-400 font-mono">Security Guard</span>
            </div>

            <p className="text-xs text-gray-300 leading-relaxed">
              To protect support channels against malicious spam crawlers and automated account deletion bots, please complete human verification:
            </p>

            <div className="p-3 bg-[#151819] border border-[#2A2F31] rounded-xl">
              <label className="flex items-center gap-3 text-xs text-gray-200 font-semibold cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isHumanVerified}
                  disabled={isVerifyingHuman}
                  onChange={(e) => handleToggleHumanVerify(e.target.checked)}
                  className="w-4 h-4 rounded border-[#2A2F31] bg-[#0C0E0F] text-[#14B8A6] focus:ring-[#14B8A6] cursor-pointer"
                />
                <div className="flex items-center gap-2">
                  {isVerifyingHuman ? (
                    <span className="text-amber-400 flex items-center gap-2">
                      <div className="w-3.5 h-3.5 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
                      Verifying human signature...
                    </span>
                  ) : isHumanVerified ? (
                    <span className="text-emerald-400 flex items-center gap-1.5 font-bold">
                      <ShieldCheck size={16} /> Verified Human User
                    </span>
                  ) : (
                    <span>I confirm I am a human user requesting account deletion</span>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* STEP 4: BOT-PROTECTED SUPPORT EMAIL (UNLOCKED ONLY UPON VERIFICATION) */}
          <div className="p-4 bg-[#0C0E0F] border border-[#1F2426] rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-white flex items-center gap-2">
                <Mail size={15} className="text-[#14B8A6]" />
                Support Contact Email
              </span>
              <span className="text-[10px] text-gray-400 font-mono">
                {isHumanVerified ? "Unlocked & Ready" : "Locked"}
              </span>
            </div>

            {isHumanVerified ? (
              <>
                <p className="text-xs text-gray-300 leading-relaxed">
                  Submit your deletion request directly to our verified support desk:
                </p>

                <div className="pt-1 flex items-center justify-start">
                  <ProtectedEmail
                    user="support"
                    domain="azuraos.app"
                    label="[ Click to Reveal Support Email ]"
                    className="w-full sm:w-auto"
                  />
                </div>
              </>
            ) : (
              <div className="p-3 bg-[#151819] border border-dashed border-[#2A2F31] rounded-xl flex items-center gap-2.5 text-xs text-gray-400">
                <Lock size={16} className="text-amber-400 shrink-0" />
                <span>Support contact email is locked until anti-bot verification above is completed.</span>
              </div>
            )}
          </div>

          {/* ACTION BUTTONS */}
          <div className="pt-2 border-t border-[#1F2426] space-y-3">
            <button
              type="button"
              onClick={handleGenerateMailto}
              disabled={!acknowledgedNoRefunds || !isHumanVerified}
              className="w-full py-3 bg-rose-500 hover:bg-rose-400 disabled:bg-rose-500/30 disabled:text-gray-500 text-white font-extrabold text-xs rounded-xl shadow-lg shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:cursor-not-allowed"
            >
              <Mail size={16} />
              <span>Generate Email Deletion Request (support@azuraos.app)</span>
              <ExternalLink size={14} />
            </button>

            <div className="flex items-center justify-between text-[11px] text-gray-500 px-1">
              <span>Support response SLA: 24-48 business hours</span>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-white underline cursor-pointer"
              >
                Keep My Account
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
